
import { useState, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Check, ShieldAlert, AlertTriangle } from 'lucide-react'
import { useRegistry } from '../../hooks/useWorkspace'
import { useMcpConnections } from '../../hooks/useMcpConnections'
import { getWriteSeed } from '../../engine/mcp/utils'
import { buildEditPayload, detectOrphanedWorkspaces, hasConnectionChanges, requiresConnectionRecreation } from '../../engine/mcp/connectionEditing'
import type { McpConnectionEntry } from '../../engine/mcp/types'
import './CreateMcpConnectionModal.css'

interface EditMcpConnectionModalProps {
    isOpen: boolean
    onClose: () => void
    connection: McpConnectionEntry
}

export default function EditMcpConnectionModal({ isOpen, onClose, connection }: EditMcpConnectionModalProps) {
    const { entries } = useRegistry()
    const { edit } = useMcpConnections()

    const [label, setLabel] = useState(connection.label)
    const initialSelection = useRef(new Set(connection.workspaceBlobIds))
    const [selectedBlobIds, setSelectedBlobIds] = useState<Set<string>>(
        new Set(connection.workspaceBlobIds)
    )
    const [writeAccess, setWriteAccess] = useState<Set<string>>(
        new Set(Object.keys(connection.mcpWriterSeeds ?? {}))
    )
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<{ message: string; details?: string } | null>(null)
    const [saved, setSaved] = useState(false)

    // Detect workspaces in the connection but no longer in the user's registry
    const orphanedBlobIds = useMemo(() =>
        detectOrphanedWorkspaces(connection.workspaceBlobIds, entries),
        [entries, connection.workspaceBlobIds])
    const recreateRequired = useMemo(() => requiresConnectionRecreation(connection), [connection])

    const sortedEntries = useMemo(() => {
        const pinned = initialSelection.current
        return [...entries].sort((a, b) => {
            const aPin = pinned.has(a.blobId)
            const bPin = pinned.has(b.blobId)
            if (aPin && !bPin) return -1
            if (!aPin && bPin) return 1
            return a.label.localeCompare(b.label)
        })
    }, [entries])

    if (!isOpen) return null

    const toggleSelection = (blobId: string) => {
        const entry = entries.find(e => e.blobId === blobId)
        if (!selectedBlobIds.has(blobId) && entry && !getWriteSeed(entry.capability)) return

        const next = new Set(selectedBlobIds)
        if (next.has(blobId)) {
            next.delete(blobId)
            // Also remove write access when deselecting
            if (writeAccess.has(blobId)) {
                const nextWrite = new Set(writeAccess)
                nextWrite.delete(blobId)
                setWriteAccess(nextWrite)
            }
        } else {
            next.add(blobId)
            // Auto-enable write for newly added workspaces that have write capability
            // (consistent with Create modal behavior)
            const entry = entries.find(e => e.blobId === blobId)
            if (entry && getWriteSeed(entry.capability)) {
                const nextWrite = new Set(writeAccess)
                nextWrite.add(blobId)
                setWriteAccess(nextWrite)
            }
        }
        setSelectedBlobIds(next)
    }

    const toggleWrite = (e: React.MouseEvent, blobId: string) => {
        e.stopPropagation()
        const entry = entries.find(e => e.blobId === blobId)
        if (!entry || !getWriteSeed(entry.capability)) return // Button is disabled, guard only
        const next = new Set(writeAccess)
        if (next.has(blobId)) next.delete(blobId)
        else next.add(blobId)
        setWriteAccess(next)
    }

    const handleSave = async () => {
        if (!label.trim()) {
            setError({ message: 'Please enter a connection name' })
            return
        }
        if (selectedBlobIds.size === 0) {
            setError({ message: 'Please select at least one workspace' })
            return
        }

        setIsSubmitting(true)
        setError(null)

        try {
            const { configs, mcpReaderSeeds, mcpWriterSeeds } = buildEditPayload(
                selectedBlobIds,
                writeAccess,
                entries,
                connection.mcpReaderSeeds ?? {},
                connection.mcpWriterSeeds ?? {},
                { requireDedicatedReaderSeed: true },
            )

            await edit(connection.configBlobId, label, configs, mcpReaderSeeds, mcpWriterSeeds)
            setSaved(true)
            setTimeout(() => onClose(), 1200)
        } catch (err) {
            console.error(err)
            const msg = err instanceof Error ? err.message : String(err)
            setError({ message: 'Failed to update connection.', details: msg })
        } finally {
            setIsSubmitting(false)
        }
    }

    const hasChanges = hasConnectionChanges(label, selectedBlobIds, writeAccess, connection)

    return createPortal(
        <div className="mcp-modal-overlay" onClick={onClose}>
            <div className="mcp-modal" onClick={e => e.stopPropagation()}>
                <div className="mcp-modal-header">
                    <div className="mcp-modal-title">
                        {saved ? 'Connection Updated' : 'Edit Connection'}
                    </div>
                    <button className="mcp-modal-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className="mcp-modal-body">
                    {saved ? (
                        <div style={{ textAlign: 'center', padding: '24px 0' }}>
                            <div className="mcp-success-icon">
                                <Check size={24} color="#10b981" />
                            </div>
                            <h3 style={{ color: 'var(--color-text-primary)', margin: '12px 0 4px' }}>Changes Saved</h3>
                            <p style={{ color: 'var(--color-text-tertiary)', fontSize: 14, margin: 0 }}>
                                Your MCP client will pick up the changes on the next request.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="mcp-form-group">
                                <label className="mcp-label">Connection Name</label>
                                <input
                                    type="text"
                                    className="mcp-input"
                                    value={label}
                                    onChange={e => setLabel(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {orphanedBlobIds.length > 0 && (
                                <div className="mcp-error-block" style={{ marginBottom: 16 }}>
                                    <div className="mcp-error-header" style={{ color: 'var(--color-warning, #f59e0b)' }}>
                                        <AlertTriangle size={14} />
                                        <span>{orphanedBlobIds.length} workspace{orphanedBlobIds.length > 1 ? 's' : ''} no longer in your registry</span>
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                                        {orphanedBlobIds.map(id => id.slice(0, 12) + '…').join(', ')} — will be removed on save.
                                    </div>
                                </div>
                            )}

                            {recreateRequired && (
                                <div className="mcp-error-block" style={{ marginBottom: 16 }}>
                                    <div className="mcp-error-header" style={{ color: 'var(--color-warning, #f59e0b)' }}>
                                        <AlertTriangle size={14} />
                                        <span>This connection must be recreated</span>
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                                        This connection predates the current auth-v2 revoke model and does not carry dedicated MCP reader credentials for every workspace. Revoke it and create a new connection instead of editing it.
                                    </div>
                                </div>
                            )}

                            <div className="mcp-form-group">
                                <label className="mcp-label">Shared Workspaces</label>
                                <div className="mcp-workspace-list">
                                    {sortedEntries.map(ws => {
                                        const isSelected = selectedBlobIds.has(ws.blobId)
                                        const canWrite = !!getWriteSeed(ws.capability)
                                        const writeEnabled = writeAccess.has(ws.blobId)
                                        return (
                                            <div
                                                key={ws.blobId}
                                                className="mcp-ws-item"
                                                onClick={() => toggleSelection(ws.blobId)}
                                                title={!canWrite ? 'MCP connections now require writer/manage access for revocable reader credentials.' : undefined}
                                            >
                                                <div className="mcp-checkbox">
                                                    {isSelected ? <Check size={14} /> : null}
                                                </div>
                                                <span className="mcp-ws-emoji">{ws.emoji || '📁'}</span>
                                                <div className="mcp-ws-info">
                                                    <div className="mcp-ws-label">{ws.label}</div>
                                                    <div className="mcp-ws-hash">{ws.blobId}</div>
                                                </div>

                                                {isSelected && (
                                                    <div className="mcp-ws-perms">
                                                        <button
                                                            type="button"
                                                            className={`mcp-perm-btn ${!writeEnabled ? 'active' : ''}`}
                                                            onClick={(e) => { e.stopPropagation(); if (writeEnabled) toggleWrite(e, ws.blobId) }}
                                                        >
                                                            Read
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`mcp-perm-btn ${writeEnabled ? 'active' : ''}`}
                                                            onClick={(e) => toggleWrite(e, ws.blobId)}
                                                            disabled={!canWrite}
                                                            title={!canWrite ? 'You are a read-only member of this workspace' : undefined}
                                                        >
                                                            Read & Write
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            {error && (
                                <div className="mcp-error-block">
                                    <div className="mcp-error-header">
                                        <ShieldAlert size={14} />
                                        <span>{error.message}</span>
                                    </div>
                                    {error.details && (
                                        <details className="mcp-error-details">
                                            <summary>Show details</summary>
                                            <pre>{error.details}</pre>
                                        </details>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="mcp-modal-footer">
                    {!saved && (
                        <>
                            <button className="mcp-btn mcp-btn-secondary" onClick={onClose}>Cancel</button>
                            <button
                                className="mcp-btn mcp-btn-primary"
                                onClick={handleSave}
                                disabled={isSubmitting || !hasChanges || recreateRequired}
                            >
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    )
}
